import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, List, ListItemButton, Stack,
  Chip, IconButton, Button, CircularProgress,
} from '@mui/material';
import { LocalOffer, ArrowBack } from '@mui/icons-material';
import { useLazyGetPosDiscountsQuery } from '../../../services/discountApi';
import DiscountList from './DiscountList';
import DiscountDetailPanel from './DiscountDetailPanel';

const scrollbar = {
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
};

const ItemDiscountTab = ({
  cart,
  orderCartItems,
  onSelectionChange,
  onRemoveItem,
  removingItemId,
}) => {
  const [step, setStep]                 = useState('pick_item');
  const [selectedItem, setSelectedItem] = useState(null); // { cartItem, oci }
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [customValue, setCustomValue]           = useState('');
  const [reason, setReason]                     = useState('');

  const [fetchItemDiscounts, { data: itemDiscountsData, isFetching: isLoadingDiscounts }] =
    useLazyGetPosDiscountsQuery();

  useEffect(() => {
    onSelectionChange({ discount: selectedDiscount, customValue, reason, item: selectedItem });
  }, [selectedDiscount, customValue, reason, selectedItem, onSelectionChange]);

  // Tracked via ref (not a dependency) so this effect only re-runs when
  // orderCartItems itself changes — keying it on `selectedItem` too made it
  // depend on the very state it sets below, which re-triggered the effect
  // every time and (combined with re-renders from the reason/value fields)
  // could run away into "Maximum update depth exceeded".
  const selectedItemRef = useRef(selectedItem);
  selectedItemRef.current = selectedItem;

  useEffect(() => {
    const current = selectedItemRef.current;
    if (!current?.oci) return;

    const latestOrderCartItem = orderCartItems.find(
      (oi) => oi.order_item_id === current.oci.order_item_id
    );

    if (!latestOrderCartItem) {
      setStep('pick_item');
      setSelectedItem(null);
      setSelectedDiscount(null);
      setCustomValue('');
      setReason('');
      return;
    }

    if (latestOrderCartItem !== current.oci) {
      setSelectedItem({ ...current, oci: latestOrderCartItem });
    }
  }, [orderCartItems]);

  const itemDiscounts = itemDiscountsData?.data?.discounts || [];

  const handlePickItem = (cartItem) => {
    const oci = orderCartItems.find((oi) => oi.product_id === cartItem.id);
    setSelectedItem({ cartItem, oci });
    setSelectedDiscount(null);
    setCustomValue('');
    setReason('');
    fetchItemDiscounts({ applicable_level: 'line_item', product_id: cartItem.id });
    setStep('pick_discount');
  };

  const handleBack = () => {
    setStep('pick_item');
    setSelectedItem(null);
    setSelectedDiscount(null);
    setCustomValue('');
    setReason('');
  };

  const handleSelectDiscount = (d) => {
    setSelectedDiscount((prev) => (prev?.id === d.id ? null : d));
    setCustomValue('');
    setReason('');
  };

  // ── Step 1: Pick Cart Item ─────────────────────────────────────
  if (step === 'pick_item') {
    return (
      <Box sx={{ ...scrollbar, overflowY: 'auto', flexGrow: 1 }}>
        <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem' }}>
            SELECT ITEM
          </Typography>
        </Box>

        {cart.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <LocalOffer sx={{ fontSize: 36, color: '#CBD5E1', mb: 1 }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#94A3B8', fontWeight: 600 }}>
              Add items to cart first
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {cart.map((item) => {
              const oci = orderCartItems.find((oi) => oi.product_id === item.id);
              return (
                <ListItemButton
                  key={item.id}
                  onClick={() => handlePickItem(item)}
                  sx={{ py: { xs: 1.25, md: 1 }, px: 2, borderBottom: '1px solid #F1F5F9', '&:hover': { bgcolor: '#F8FAFC' } }}
                >
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: { xs: '0.88rem', md: '0.82rem' }, fontWeight: 700, color: '#1E293B' }}>
                      {item.name}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.2, flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>
                        ×{item.qty} · ₹{item.retail_price}
                      </Typography>
                      {oci?.discount_name && (
                        <Chip
                          label={`🏷 ${oci.discount_name}`}
                          size="small"
                          sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#F0FDF4', color: '#16A34A' }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#4F46E5', ml: 1, flexShrink: 0 }}>
                    ₹{(parseFloat(item.retail_price || 0) * (item.qty || 1)).toFixed(2)}
                  </Typography>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    );
  }

  // ── Step 2: Pick Discount for selected item ────────────────────
  const oci = selectedItem?.oci;
  const itemSubtotal = oci
    ? parseFloat(oci.unit_price || 0) * parseFloat(oci.quantity || 1)
    : parseFloat(selectedItem?.cartItem?.retail_price || 0) * (selectedItem?.cartItem?.qty || 1);

  return (
    <Box sx={{ ...scrollbar, overflowY: 'auto', flexGrow: 1 }}>

      {/* Breadcrumb */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton size="small" onClick={handleBack} sx={{ color: '#64748B' }}>
          <ArrowBack sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1E293B' }}>
          {selectedItem?.cartItem?.name}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', ml: 'auto', flexShrink: 0 }}>
          ₹{itemSubtotal.toFixed(2)}
        </Typography>
      </Box>

      {/* Currently applied on this item — with remove */}
      {oci?.discount_name && (
        <Box sx={{ mx: 2, mb: 1.5 }}>
          <Box sx={{
            bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px',
            px: 1.5, py: 0.85, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Typography noWrap sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#166534', flexGrow: 1, minWidth: 0 }}>
              ✓ {oci.discount_name} applied
            </Typography>
            <Button
              size="small"
              onClick={() => onRemoveItem(oci.order_item_id)}
              disabled={removingItemId === oci.order_item_id}
              sx={{ fontSize: '0.68rem', color: '#EF4444', textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0.75, flexShrink: 0, '&:hover': { bgcolor: '#FEF2F2' } }}
            >
              {removingItemId === oci.order_item_id ? <CircularProgress size={14} color="error" /> : 'Remove'}
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ px: 2, pb: 0.5 }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.05rem' }}>
          AVAILABLE DISCOUNTS
        </Typography>
      </Box>

      {isLoadingDiscounts ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: '#4F46E5' }} />
        </Box>
      ) : (
        <DiscountList
          discounts={itemDiscounts}
          selectedId={selectedDiscount?.id}
          onSelect={handleSelectDiscount}
          emptyText="No item discounts available for this product"
        />
      )}

      {selectedDiscount && (
        <DiscountDetailPanel
          discount={selectedDiscount}
          orderSubtotal={itemSubtotal}
          customValue={customValue}
          onCustomValueChange={setCustomValue}
          reason={reason}
          onReasonChange={setReason}
        />
      )}
    </Box>
  );
};

export default ItemDiscountTab;
